import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from '../styles/ServiceManager.module.css';

const serviceNames2 = {
  avitoPoolWorker: "Делит задания для статуса Avito",
  avitoGetWorker: "Получение статы из Avito",
  butch_to_google: "Стата и статус в Google",
  template_to_g: "Шаблон → Google",
  balance_report_av: "Баланс Avito",
  telegram: "Телеграм",
  excel_to_g: "Excel → Google",
  xml_to_avito: "пути к XML для Авито",
  video_servers_api: "Апи для видеоворкеров",
};

const serviceNames = {
  google_xml: "Создание XML из Google",
  googleStatusWorker: "Статус Google",
  googleStatusWorker2: "Статус Google2",
  googleStatusWorker3: "Статус Google3",
  quart_pool: "Гугл аккаунты",
  bd_queue: "Очередь БД",
  avitostavki: "Авито ставки",
  yandex_photo: "Яндекс фото",
  "new-five-ads": "Новый сайт БЭК",
};

const primaryActions = [
  { value: "logs", label: "Логи" },
  { value: "start", label: "▶" },
  { value: "stop", label: "■" },
  { value: "restart", label: "↻" }
];

const moreActions = [
  { value: "status", label: "ℹ" },
  { value: "enable", label: "Автозапуск" },
  { value: "disable", label: "✖ Автозапуск" }
];

export default function ServiceManagerWs() {
  const [statuses, setStatuses] = useState({});
  const [outputLines, setOutputLines] = useState([]);
  const [serviceMessages, setServiceMessages] = useState([]);
  const [visibleLines, setVisibleLines] = useState(1);
  const [outputHeight, setOutputHeight] = useState(260);
  const [error, setError] = useState("");
  const [currentService, setCurrentService] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectTick, setReconnectTick] = useState(0);
  const [isServicesCollapsed, setIsServicesCollapsed] = useState(false);
  const wsRef = useRef(null);
  const isManualCloseRef = useRef(false);
  const outputRef = useRef(null);

  const wsUrl = useMemo(() => {
    const protocol = window.location.protocol === "https:" ? "wss://" : "ws://";
    return `${protocol}${window.location.host}/api/admin/service/ws`;
  }, []);

  const output = useMemo(() => {
    return outputLines.slice(-visibleLines).join("\n");
  }, [outputLines, visibleLines]);

  const sendAction = (payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setError("WebSocket не подключен");
      return;
    }
    ws.send(JSON.stringify(payload));
  };

  const requestStatusAll = () => {
    sendAction({ action: "status_all" });
  };

  const stopLogs = () => {
    sendAction({ action: "stop_logs" });
  };

  const reconnectWebSocket = () => {
    setError("");
    setIsConnected(false);

    const ws = wsRef.current;
    if (ws) {
      isManualCloseRef.current = true;
      try {
        ws.close();
      } catch (closeError) {
        void closeError;
      }
      wsRef.current = null;
    }

    setReconnectTick((prev) => prev + 1);
  };

  const splitToLines = (text) => {
    return String(text || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "")
      .split("\n");
  };

  const appendOutputLines = (incoming) => {
    setOutputLines((prev) => {
      const next = [...prev, ...splitToLines(incoming)];
      return next.slice(-5000);
    });
  };

  const pushServiceMessage = (incoming) => {
    const line = String(incoming || "").replace(/\s+/g, " ").trim();
    if (!line) {
      return;
    }

    setServiceMessages((prev) => [...prev, line].slice(-6));
  };

  const getCompactMessage = (msg) => {
    if (!msg || typeof msg !== "object") {
      return "";
    }

    if (msg.message) {
      return msg.message;
    }

    if (msg.error) {
      return `Ошибка: ${msg.error}`;
    }

    if (msg.result) {
      return String(msg.result);
    }

    if (msg.output) {
      return String(msg.output);
    }

    if (msg.type === "command_finished") {
      const service = msg.service ? ` ${msg.service}` : "";
      const action = msg.action ? ` ${msg.action}` : "";
      return `Команда завершена:${service}${action} код=${msg.code}`.trim();
    }

    return JSON.stringify(msg);
  };

  const handleAction = (service, action) => {
    setCurrentService(service);
    if (action === "logs") {
      setOutputLines([]);
    }
    pushServiceMessage(`Команда: ${service} → ${action}`);
    sendAction({ service, action });
  };

  useEffect(() => {
    isManualCloseRef.current = false;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setError("");
      pushServiceMessage("WS соединение установлено");
      ws.send(JSON.stringify({ action: "status_all" }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === "log") {
          appendOutputLines(msg.line || "");
          return;
        }

        if (msg.type === "ready") {
          pushServiceMessage(getCompactMessage(msg));
          return;
        }

        if (msg.type === "status_all") {
          setStatuses(msg.statuses || msg.data || {});
          pushServiceMessage("Статусы обновлены");
          return;
        }

        if (msg.type === "status" && msg.service) {
          setStatuses((prev) => ({
            ...prev,
            [msg.service]: msg.status || msg.state || "Неизвестно"
          }));
          pushServiceMessage(`${msg.service}: ${msg.status || msg.state || "Неизвестно"}`);
          return;
        }

        if (msg.type === "error") {
          const message = msg.message || "Ошибка WS";
          setError(message);
          pushServiceMessage(`❌ ${message}`);
          return;
        }

        if (msg.type === "info") {
          pushServiceMessage(msg.message || "Инфо");
          return;
        }

        if (msg.type === "command_finished") {
          pushServiceMessage(getCompactMessage(msg));
          return;
        }

        if (msg.error) {
          pushServiceMessage(`❌ ${msg.error}`);
          return;
        }

        if (msg.output || msg.result) {
          appendOutputLines(msg.output || msg.result || "");
          return;
        }

        pushServiceMessage(getCompactMessage(msg));
      } catch (parseError) {
        pushServiceMessage(`❌ Ошибка разбора сообщения: ${String(parseError)}`);
      }
    };

    ws.onerror = () => {
      setError("Ошибка WebSocket");
      pushServiceMessage("❌ Ошибка WebSocket");
    };

    ws.onclose = () => {
      setIsConnected(false);
      if (!isManualCloseRef.current) {
        setError("Соединение WebSocket закрыто");
        pushServiceMessage("WS соединение закрыто");
      }
    };

    return () => {
      isManualCloseRef.current = true;
      try {
        ws.close();
      } catch (closeError) {
        void closeError;
      }
    };
  }, [wsUrl, reconnectTick]);

  useEffect(() => {
    const element = outputRef.current;
    if (!element) return undefined;

    const recalculateVisibleLines = () => {
      const computed = window.getComputedStyle(element);
      let lineHeight = Number.parseFloat(computed.lineHeight);
      if (Number.isNaN(lineHeight)) {
        const fontSize = Number.parseFloat(computed.fontSize) || 16;
        lineHeight = fontSize * 1.2;
      }

      const paddingTop = Number.parseFloat(computed.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(computed.paddingBottom) || 0;
      const contentHeight = Math.max(0, element.clientHeight - paddingTop - paddingBottom);
      const byHeight = Math.floor(contentHeight / lineHeight);
      setVisibleLines(Math.max(1, byHeight));
    };

    const recalculateOutputHeight = () => {
      const rect = element.getBoundingClientRect();
      const bottomGap = 24;
      const nextHeight = Math.floor(window.innerHeight - rect.top - bottomGap);
      setOutputHeight(Math.max(140, nextHeight));
    };

    const recalculateAll = () => {
      recalculateOutputHeight();
      recalculateVisibleLines();
    };

    recalculateAll();

    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(recalculateAll)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(element);
    }

    window.addEventListener("resize", recalculateAll);

    return () => {
      window.removeEventListener("resize", recalculateAll);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isServicesCollapsed]);

  return (
    <div className={styles.super_container}>
      <div style={{ marginBottom: "5px" }}>
        <button
          className={styles.actionButton}
          onClick={() => setIsServicesCollapsed((prev) => !prev)}
        >
          {isServicesCollapsed ? "Развернуть службы" : "Свернуть службы"}
        </button>
        <button
          className={styles.actionButton}
          onClick={requestStatusAll}
          disabled={!isConnected}
        >
          Обновить статусы
        </button>
        <button
          className={styles.actionButton}
          onClick={stopLogs}
          disabled={!isConnected}
          style={{ marginLeft: "8px" }}
        >
          Остановить логи
        </button>
        <button
          className={styles.actionButton}
          onClick={reconnectWebSocket}
          style={{ marginLeft: "8px" }}
        >
          Переподключиться
        </button>
        <span style={{ marginLeft: "12px", color: isConnected ? "green" : "red" }}>
          {isConnected ? "WS подключен" : "WS отключен"}
        </span>
        {!!error && (
          <span style={{ marginLeft: "12px", color: "red" }}>
            ❌ {error}
          </span>
        )}
        <span style={{ marginLeft: "12px" }}>
          Влезает строк: {visibleLines}
        </span>
      </div>
      {!isServicesCollapsed && (
        <div className={styles.service_div}>
          <div className={styles.servicesContainer}>
            {Object.entries(serviceNames).map(([key, name]) => (
              <div key={key} className={styles.service}>
                <span className={styles.serviceName}>
                  {name} ({key})
                </span>
                <span
                  className={styles.serviceStatus}
                  style={{ color: statuses[key] === "Запущено" ? "green" : "red" }}
                >
                  {statuses[key] || "Загрузка..."}
                </span>
                {primaryActions.map((action) => (
                  <button
                    key={action.value}
                    onClick={() => handleAction(key, action.value)}
                    className={styles.actionButton}
                    disabled={!isConnected}
                  >
                    {action.label}
                  </button>
                ))}

                <select
                  className={`${styles.actionButton} ${styles.select_button}`}
                  disabled={!isConnected}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAction(key, e.target.value);
                      e.target.selectedIndex = 0;
                    }
                  }}
                >
                  <option value="">⚙ Ещё</option>
                  {moreActions.map((action) => (
                    <option key={action.value} value={action.value}>
                      {action.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className={styles.servicesContainer}>
            {Object.entries(serviceNames2).map(([key, name]) => (
              <div key={key} className={styles.service}>
                <span className={styles.serviceName}>
                  {name} ({key})
                </span>
                <span
                  className={styles.serviceStatus}
                  style={{ color: statuses[key] === "Запущено" ? "green" : "red" }}
                >
                  {statuses[key] || "Загрузка..."}
                </span>
                {primaryActions.map((action) => (
                  <button
                    key={action.value}
                    onClick={() => handleAction(key, action.value)}
                    className={styles.actionButton}
                    disabled={!isConnected}
                  >
                    {action.label}
                  </button>
                ))}

                <select
                  className={`${styles.actionButton} ${styles.select_button}`}
                  disabled={!isConnected}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAction(key, e.target.value);
                      e.target.selectedIndex = 0;
                    }
                  }}
                >
                  <option value="">⚙ Ещё</option>
                  {moreActions.map((action) => (
                    <option key={action.value} value={action.value}>
                      {action.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        <div className={styles.serviceMetaLog}>
        {serviceMessages.length > 0 ? serviceMessages.join(" | ") : "Служебные сообщения появятся здесь"}
      </div>
        </div>
      )}
      <h3 className={styles.h2}>
        Результат{currentService ? `: ${serviceNames[currentService] || serviceNames2[currentService] || currentService}` : ""}
      </h3>
      <pre ref={outputRef} style={{ background: "#f0f0f0", padding: "10px", whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere", margin: "0", minHeight: "140px", maxHeight: `${outputHeight}px`, height: `${outputHeight}px`, overflow: "auto" }}>
        {output}
      </pre>
    </div>
  );
}